import IconCancel from '@mui/icons-material/Cancel';
import IconContentAdd from '@mui/icons-material/GetApp';
import { Box, CircularProgress, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Papa from "papaparse";
import { Fragment, useState } from 'react';
import { ArrayField, Button, Datagrid, FileInput, FormDataConsumer, FunctionField, ReferenceInput, SelectInput, SimpleForm, SimpleShowLayout, useDataProvider, useNotify, useRecordContext } from 'react-admin';
import * as XLSX from 'xlsx';
import { getChainId } from '../../lib';
import { parseSaleProductRow } from '../../lib/parseSaleProductRow';

const ImportButton = ({ chainId, postCommitCallback }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [conflictSaleProducts, setConflictSaleProducts] = useState([]);
  const [csvConflictSaleProducts, setCsvConflictSaleProducts] = useState([]);
  const [isLoading, setIsloading] = useState(false);
  const [showConflictList, setShowConflictList] = useState(false);

  const findSaleProductsApi = (filter, xmlSaleProducts) => {
    setIsloading(true);
    dataProvider.create('chains/chainId/sale-products/check-conflict', { data: { filter } })
      .then(({ data }) => {
        const withConflicts = data.saleProducts.filter(item => {
          const xmlSaleProduct = xmlSaleProducts.find(xmlSP => xmlSP.code === item.code && (xmlSP.name !== item.name || +xmlSP.pointsPerMoney !== +item.pointsPerMoney));
          return xmlSaleProduct;
        });

        const withoutConflicts = xmlSaleProducts.filter(xmlSaleProduct => !data.saleProducts.find(item => item.code === xmlSaleProduct.code));

        if (withConflicts.length) {
          setConflictSaleProducts(withConflicts);
          setShowConflictList(true);
          setIsloading(false);
          if (withoutConflicts.length) {
            importSaleProductApi(withoutConflicts);
          }
        } else if (withoutConflicts.length) {
          importSaleProductApi(withoutConflicts, postCommitCallback);
        } else if (!withoutConflicts.length) {
          notify('Arquivo já foi importado', { type: 'info' });
          postCommitCallback();
        } else if (!data.count) {
          notify('Arquivo está vazio', { type: 'info' });
          postCommitCallback();
        }
      })
      .catch(() => {
        notify('Não foi possível carregar o arquivo', { type: 'error' });
        setIsloading(false);
      });
  }

  const importSaleProductApi = (data, onSuccessCallback) => {
    dataProvider.create('chains/chainId/sale-products', { data: data.map(item => ({ ...item, chainId })) })
      .then(({ data }) => {
        notify(`O total de ${data.newSaleProducts.length} registros foram importados`, { type: 'info' });
      })
      .catch((error) => {
        notify(error.message || `Não foi possível realizar a importação do arquivo`, { type: 'error' });
      })
      .finally(() => {
        setIsloading(false);
        onSuccessCallback && onSuccessCallback();
      });
  }

  const xlsxParse = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet);

        resolve(json);
      };

      reader.readAsArrayBuffer(file);
    });
  }

  const csvParse = (file) => {
    return new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          resolve(result.data);
        },
      });
    });
  }

  const handleFileUpload = async (file) => {
    const fileName = file.name.toLowerCase();

    let data = [];

    if (fileName.endsWith('.csv')) {
      data = await csvParse(file);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      data = await xlsxParse(file);
    } else {
      notify('Arquivo não suportado', { type: 'warning' });
      return;
    }

    const formatedSaleProducts = data.map(parseSaleProductRow).filter(row => row.code && row.name);

    const uniqueSaleProducts = Array.from(new Map(formatedSaleProducts.map(obj => [obj.code, obj])).values());

    setCsvConflictSaleProducts(uniqueSaleProducts);
    findSaleProductsApi({ codes: uniqueSaleProducts.map(item => item.code), isDisabled: 'all', chainId }, uniqueSaleProducts);
  };

  const getSalesProductsWithConflicts = () => {
    return conflictSaleProducts.map(saleProduct => ({ ...saleProduct, newSaleProduct: csvConflictSaleProducts.find(xmlConflictSaleProduct => xmlConflictSaleProduct.code === saleProduct.code) }));
  }

  const ApproveAllButton = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [isLoading, setIsLoading] = useState(false);

    const approveAllProducts = async () => {
      setIsLoading(true);

      try {
        const productsToUpdate = getSalesProductsWithConflicts();
        const batchSize = 500;
        let totalUpdated = 0;

        for (let i = 0; i < productsToUpdate.length; i += batchSize) {
          const batch = productsToUpdate.slice(i, i + batchSize);

          const batchPromises = batch.map(record =>
            dataProvider.create('chains/chainId/sale-products', {
              id: record.id,
              data: [{ ...record.newSaleProduct, chainId }]
            })
          );

          await Promise.all(batchPromises);
          totalUpdated += batch.length;
        }

        notify(`${totalUpdated} registros foram atualizados`, { type: 'success' });
        setShowConflictList(false);
        postCommitCallback && postCommitCallback();
      } catch (error) {
        notify('Erro ao atualizar os produtos', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Button
        startIcon={isLoading ? <CircularProgress size={16} /> : null}
        disabled={isLoading}
        label="Aprovar Todos"
        variant="contained"
        color="primary"
        onClick={approveAllProducts}
        sx={{ marginBottom: 2 }}
      />
    );
  };

  const ActionConflictButtons = () => {
    const [isDisabled, setIsDisabled] = useState(false);

    const postCommit = () => {
      setIsDisabled(true);
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <KeepButton isDisabled={isDisabled} postCommit={postCommit} />
        <IgnoreButton isDisabled={isDisabled} postCommit={postCommit} />
      </div>
    );
  }

  const KeepButton = ({ isDisabled, postCommit }) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [isLoading, setIsLoading] = useState(false);
    const [checked, setChecked] = useState(false);

    const { newSaleProduct, ...saleProducts } = record;

    const updateRequest = () => {
      setIsLoading(true);
      dataProvider.create('chains/chainId/sale-products', { id: saleProducts.id, data: [{ ...newSaleProduct, chainId }] })
        .then(({ data }) => {
          setChecked(true);
          postCommit && postCommit();
          notify('Um registro foi atualizado', { messageArgs: { smart_count: 1 } });
        })
        .catch(() => {
          notify('Não possível atualizar um registro');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return (
      <Button startIcon={isLoading ? <CircularProgress size={16} /> : <></>} disabled={isLoading || isDisabled} label="Sim" variant={checked ? 'contained' : 'outlined'} onClick={updateRequest} />
    );
  }

  const IgnoreButton = ({ isDisabled, postCommit }) => {
    const notify = useNotify();
    const [checked, setChecked] = useState(false);

    const ignoreItem = () => {
      notify('registro ignorado');
      setChecked(true);
      postCommit && postCommit();
    }

    return (
      <Button disabled={isDisabled} label="Não" variant={checked ? 'contained' : 'outlined'} onClick={ignoreItem} />
    );
  }

  return (
    <Box width="100%" marginTop={3}>
      {
        showConflictList ?
          <>
            <Typography variant="body1">Os registros abaixo entram em conflito com os já existentes no banco de dados. Escolha se deseja atualizar os registros atuais com os dados importados.</Typography>
            <Box sx={{ marginTop: 2, marginBottom: 2 }}>
              <ApproveAllButton />
            </Box>
            <SimpleShowLayout sx={{ width: '100%', padding: 0, marginTop: 2 }} record={{ saleProducts: getSalesProductsWithConflicts() }}>
              <ArrayField source="saleProducts" label="Produtos de venda">
                <Datagrid bulkActionButtons={false}>
                  <FunctionField label="Nome" render={record => (
                    <>
                      <Typography variant="body1">Atual: {record.name}</Typography>
                      {
                        record.newSaleProduct ?
                          <Typography variant="body2" color="textSecondary">Arquivo: {record.newSaleProduct.name}</Typography>
                          :
                          <></>
                      }
                    </>
                  )} />
                  <FunctionField label="Código" render={record => (
                    <>
                      <Typography variant="body1">Atual: {record.code}</Typography>
                      {
                        record.newSaleProduct ?
                          <Typography variant="body2" color="textSecondary">Arquivo: {record.newSaleProduct.code}</Typography>
                          :
                          <></>
                      }
                    </>
                  )} />
                  <FunctionField label="Pontuação por Real" render={record => (
                    <>
                      <Typography variant="body1">Atual: {Number(record.pointsPerMoney).toFixed(2).replace('.', ',')}</Typography>
                      {
                        record.newSaleProduct ?
                          <Typography variant="body2" color="textSecondary">Arquivo: {Number(record.newSaleProduct.pointsPerMoney).toFixed(2).replace('.', ',')}</Typography>
                          :
                          <></>
                      }
                    </>
                  )} />
                  <ActionConflictButtons label="Atualizar?" />
                </Datagrid>
              </ArrayField>
            </SimpleShowLayout>
          </>
          :
          isLoading ?
            <Box display="flex" flexDirection="row" gap={2} width="100%" justifyContent="center">
              <CircularProgress size={22} />
              <Typography variant="body1" color="textSecondary">Carregando o arquivo...</Typography>
            </Box>
            :
            <FileInput
              source=" "
              accept={{ 'text/csv': ['.csv'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] }}
              placeholder="Selecione um arquivo (.csv, .xlsx, .xls)"
              options={{
                onDrop: (acceptedFiles) => handleFileUpload(acceptedFiles[0]),
              }}
            />
      }
    </Box>
  );
}

export default () => {
  const [showDialog, setShowDialog] = useState(false);

  const handleClick = () => {
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false)
  };

  return (
    <Fragment>
      <Button onClick={handleClick} label="Importar arquivo">
        <IconContentAdd style={{ transform: "rotate(180deg)", fontSize: "20" }} />
      </Button>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={closeDialog}
        aria-label="Importar arquivo"
        maxWidth="md"
      >
        <DialogTitle>Importar arquivo</DialogTitle>
        <DialogContent>
          <SimpleForm
            toolbar={null}
            resource="chains/chainId/sale-products"
            defaultValues={{ chainId: getChainId() }}
          >
            <Typography style={{ marginTop: 5 }}>
              Importar uma planilha com as colunas nessa ordem, independente do nome da coluna: Nome, Código e Pontuação por real
            </Typography>
            <ReferenceInput
              source="chainId"
              reference="chains"
              perPage={null}
              sort={{ field: "name", order: "ASC" }}
            >
              <SelectInput label="Rede" optionText="name" isRequired />
            </ReferenceInput>
            <FormDataConsumer>
              {({ formData }) => (
                <ImportButton
                  chainId={formData.chainId || getChainId()}
                  postCommitCallback={() => {
                    closeDialog();
                  }}
                />
              )}
            </FormDataConsumer>
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button label="ra.action.close" onClick={closeDialog}>
            <IconCancel />
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );

}